import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createProviderRegistrationCheckout,
  createTenantFamilyRegistrationCheckout,
  createTenantStripeOnboardingLink,
  getTenantStripeConnectStatus,
} from '../lib/payments';
import type {
  TenantStripeCheckoutSessionRequest,
  TenantStripeCheckoutSessionResponse,
  TenantStripeConnectOnboardingLinkRequest,
  TenantStripeConnectOnboardingLinkResponse,
  TenantStripeConnectStatus,
} from '../types';
import { tenantFamilyKeys } from './useTenantFamily';

export const tenantStripePaymentKeys = {
  all: ['tenant-stripe-payments'] as const,
  status: () => [...tenantStripePaymentKeys.all, 'status'] as const,
};

export function useTenantStripeConnectStatus(enabled: boolean = true) {
  return useQuery<TenantStripeConnectStatus>({
    queryKey: tenantStripePaymentKeys.status(),
    queryFn: getTenantStripeConnectStatus,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useTenantStripeOnboardingLink() {
  const queryClient = useQueryClient();
  return useMutation<TenantStripeConnectOnboardingLinkResponse, Error, TenantStripeConnectOnboardingLinkRequest>({
    mutationFn: createTenantStripeOnboardingLink,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantStripePaymentKeys.status() });
    },
  });
}

export function useTenantFamilyRegistrationCheckout() {
  const queryClient = useQueryClient();
  return useMutation<TenantStripeCheckoutSessionResponse, Error, { registrationId: number; payload: TenantStripeCheckoutSessionRequest }>({
    mutationFn: ({ registrationId, payload }) => createTenantFamilyRegistrationCheckout(registrationId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.schedule() });
    },
  });
}

export function useProviderRegistrationCheckout(providerId: number) {
  return useMutation<TenantStripeCheckoutSessionResponse, Error, { registrationId: number; payload: TenantStripeCheckoutSessionRequest }>({
    mutationFn: ({ registrationId, payload }) => createProviderRegistrationCheckout(providerId, registrationId, payload),
  });
}
